if (!customElements.get('madara-product-ugc')) {
  customElements.define(
    'madara-product-ugc',
    class MadaraProductUgc extends HTMLElement {

      // ─────────────────────────────────────────────────────────────────────
      // Lifecycle
      // ─────────────────────────────────────────────────────────────────────

      connectedCallback() {
        this.modal = this.querySelector('.madara-product-ugc__modal');
        this.modalIndex = 0;
        this._isNavigating = false;
        this._breakpointMql = window.matchMedia('(min-width: 768px)');

        document.body.appendChild(this.modal);

        this.initSlider();
        this.initAutoplay();
        this.initModal();
        this.initBreakpointWatcher();
      }

      disconnectedCallback() {
        this._unlockBodyScroll();
        this._detachViewportWatcher();
        this.modal?.remove();
        document.removeEventListener('keydown', this._onKeydown);
        this._breakpointMql?.removeEventListener('change', this._onBreakpointChange);
      }

      // Close the modal when crossing the mobile/desktop breakpoint.
      // Layout, slot height, drag handle, and body-scroll-lock all depend on
      // this breakpoint, so re-opening gives a consistent state instead of
      // trying to patch the in-flight modal to match the new viewport.
      initBreakpointWatcher() {
        this._onBreakpointChange = () => {
          if (this.modal?.classList.contains('is-open')) {
            this.closeModal();
          }
        };

        this._breakpointMql.addEventListener('change', this._onBreakpointChange);
      }

      // ─────────────────────────────────────────────────────────────────────
      // Helpers
      // ─────────────────────────────────────────────────────────────────────

      get slides() {
        return [...this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')];
      }

      get ugcCards() {
        return [...this.querySelectorAll('.madara-product-ugc__media')];
      }

      // ─────────────────────────────────────────────────────────────────────
      // Carousel autoplay
      // ─────────────────────────────────────────────────────────────────────

      initSlider() {
        this.sliderInstance = new FoxTheme.Carousel(this.querySelector('.swiper'), {
          slidesPerView: 'auto',
          spaceBetween: 8,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          threshold: 2,
          grabCursor: true,
        });
        this.sliderInstance.init();
      }

      initAutoplay() {
        this.slides.forEach((slide, index) => {
          const video = slide.querySelector('.madara-product-ugc__video');
          const mediaCard = slide.querySelector('.madara-product-ugc__media');

          if (!video || !mediaCard) return;

          const startProgress = (video) => {
            if (video.duration && video.duration > 0) {
              mediaCard.style.setProperty('--ugc-duration', `${video.duration}s`);
              mediaCard.classList.remove('is-playing');
              void mediaCard.offsetHeight; // force reflow so animation resets
              mediaCard.classList.add('is-playing');
            }
          };

          video.addEventListener('play', () => {
            if (video.duration && video.duration > 0) {
              // Duration known — start animation immediately with correct duration
              startProgress(video);
            } else {
              // Duration not yet known (cold cache, no preload) — show playing
              // state now and restart the animation once duration arrives
              mediaCard.classList.add('is-playing');
              video.addEventListener('loadedmetadata', () => {
                if (!video.paused) {
                  startProgress(video);
                }
              }, { once: true });
            }
          });

          video.addEventListener(
            'pause',
            () => mediaCard.classList.remove('is-playing')
          );

          // Buffering, whether on a slow start or mid-playback, shows a spinner
          ['waiting', 'stalled'].forEach((type) =>
            video.addEventListener(type, () => mediaCard.classList.add('is-loading'))
          );

          ['playing', 'pause', 'ended', 'error'].forEach((type) =>
            video.addEventListener(type, () => mediaCard.classList.remove('is-loading'))
          );

          video.addEventListener(
            'ended',
            () => {
              mediaCard.classList.remove('is-playing');
              this._playSlide(index + 1);
            }
          );
        });

        setTimeout(() => this._playSlide(0), 150);
      }

      _playSlide(index) {
        const slides = this.slides;

        if (index >= slides.length) return;

        slides.forEach((slide) => {
          const video = slide.querySelector('.madara-product-ugc__video');

          if (video && !video.paused) {
            video.pause()
          };
        });

        // For not disabled scroll to next slide when
        // const swiperEl = this.querySelector('.swiper');s
        // if (swiperEl?.swiper) swiperEl.swiper.slideTo(index);

        const video = slides[index]?.querySelector('.madara-product-ugc__video');

        if (video && video.readyState < 3 /* HAVE_FUTURE_DATA */) {
          slides[index]
            ?.querySelector('.madara-product-ugc__media')
            ?.classList.add('is-loading');
        }

        video?.play().catch(() => {});
      }

      // ─────────────────────────────────────────────────────────────────────
      // Modal — track initialisation
      // ─────────────────────────────────────────────────────────────────────

      initModal() {
        // Open on carousel mediacard click
        this.querySelectorAll('.madara-product-ugc__media')
          .forEach(media => {
            media.addEventListener('click', (e) => {
              e.preventDefault();
              const index = this.ugcCards.indexOf(media);
              if (index === -1) return;
              this.openModalAt(index);
            });
          })

        this.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const card = e.target.closest('.madara-product-ugc__media');
          if (!card) return;
          e.preventDefault();
          const index = this.ugcCards.indexOf(card);
          if (index === -1) return;
          this.openModalAt(index);
        });

        // Nav buttons
        this.modal
          .querySelector('.madara-product-ugc__modal-prev')
          .addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateModal(-1);
          });
        this.modal
          .querySelector('.madara-product-ugc__modal-next')
          .addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateModal(1);
          });

        this.initModalWheelListener()
        this.initModalTouchListener()
        this.initModalDrag()

        // Keyboard
        this._onKeydown = (e) => {
          if (!this.modal.classList.contains('is-open')) return;
          if (e.key === 'Escape')    { this.closeModal(); }
          if (e.key === 'ArrowDown') { e.preventDefault(); this.navigateModal(1);  }
          if (e.key === 'ArrowUp')   { e.preventDefault(); this.navigateModal(-1); }
        };
        document.addEventListener('keydown', this._onKeydown);

        // Mute toggle — applies to all slots so state persists between videos
        this.modal
          .querySelector('.video-mute-action')
          .addEventListener('click', (e) => {
            e.stopPropagation();
            const currentVideo = this._modalTrackSlides?.[1]?.querySelector('video');
            if (!currentVideo) return;
            const newMuted = !currentVideo.muted;
            this._modalTrackSlides.forEach(s => { const v = s.querySelector('video'); if (v) v.muted = newMuted; });
            this._controls?.classList.toggle('is-muted', newMuted);
          });

        // Play / pause toggle
        this.modal
          .querySelector('.video-play-action')
          .addEventListener('click', (e) => {
            e.stopPropagation();
            const video = this._modalTrackSlides?.[1]?.querySelector('video');
            if (!video) return;
            video.paused ? video.play() : video.pause();
          });

        // Auto-advance when modal video ends
        // We delegate from the modal-inner to catch whichever slot is playing
        this.modal
          .querySelector('.madara-product-ugc__modal-inner')
          .addEventListener('ended', (e) => {
            if (e.target.closest('.madara-product-ugc__modal-carousel-slide') === this._modalTrackSlides?.[1]) {
              this.navigateModal(1, true);
            }
          }, true);

        // Sync play/pause state classes
        this.modal
          .querySelector('.madara-product-ugc__modal-inner')
          .addEventListener('play', (e) => {
            if (e.target.closest('.madara-product-ugc__modal-carousel-slide') === this._modalTrackSlides?.[1]) {
              this._startControlsProgress(e.target);
            }
          }, true);

        this.modal
          .querySelector('.madara-product-ugc__modal-inner')
          .addEventListener('pause', (e) => {
            if (e.target.closest('.madara-product-ugc__modal-carousel-slide') === this._modalTrackSlides?.[1]) {
              this._controls?.classList.remove('is-playing');
            }
          }, true);

        // Buffering spinner for whichever slot is on screen
        const modalInner = this.modal.querySelector('.madara-product-ugc__modal-inner');

        ['waiting', 'stalled'].forEach((type) =>
          modalInner.addEventListener(type, (e) => {
            const slide = e.target.closest('.madara-product-ugc__modal-carousel-slide');
            if (slide && slide === this._modalTrackSlides?.[1]) slide.classList.add('is-loading');
          }, true)
        );

        ['playing', 'pause', 'ended', 'error'].forEach((type) =>
          modalInner.addEventListener(type, (e) => {
            e.target
              .closest('.madara-product-ugc__modal-carousel-slide')
              ?.classList.remove('is-loading');
          }, true)
        );

        // Close button (wired last, after controls exist)
        this.modal
          .querySelector('.madara-product-ugc__modal-close')
          .addEventListener('click', () => this.closeModal());
      }

      initModalWheelListener() {
        // Scroll wheel
        // Trackpad inertia keeps firing wheel events after the animation ends.
        // Every event that arrives while navigating pushes a cooldown deadline
        // 300 ms into the future. Once inertia truly stops, the deadline expires
        // and a fresh gesture can trigger the next navigation.
        this._wheelAccum    = 0;
        this._wheelCooldown = 0; // epoch ms — ignore wheel events before this time
        let wheelResetTimer = null;

        this.modal.addEventListener('wheel', (e) => {
          e.preventDefault();
          const now = Date.now();

          if (this._isNavigating) {
            // Keep extending the post-animation cooldown while inertia arrives
            this._wheelAccum    = 0;
            this._wheelCooldown = now + 300;
            return;
          }

          // Swallow leftover inertia that arrives right after animation ends
          if (now < this._wheelCooldown) {
            this._wheelAccum = 0;
            return;
          }

          if (e.deltaMode !== 0) {
            // Physical mouse — one notch = one video
            this.navigateModal(e.deltaY > 0 ? 1 : -1);
            return;
          }

          // Trackpad — accumulate until threshold, then navigate once
          this._wheelAccum += e.deltaY;
          clearTimeout(wheelResetTimer);
          wheelResetTimer = setTimeout(() => { this._wheelAccum = 0; }, 300);

          if (Math.abs(this._wheelAccum) >= 50) {
            const dir = this._wheelAccum > 0 ? 1 : -1;
            this._wheelAccum = 0;
            this._wheelCooldown = 0; // clear so next deliberate scroll works immediately
            this.navigateModal(dir);
          }
        }, { passive: false });
      }

      initModalTouchListener() {
        let touchStartY = 0;

        this.modal.addEventListener('touchstart', (e) => {
          // If a nav is in flight, don't record a start point — otherwise a
          // touchend arriving right after the animation settles would fire a
          // stale swipe using coordinates from before the nav began.
          if (this._isNavigating) return;
          touchStartY = e.touches[0].clientY;
        }, { passive: true });

        this.modal.addEventListener('touchend', (e) => {
          if (this._isNavigating) return;

          const delta = touchStartY - e.changedTouches[0].clientY;
          if (Math.abs(delta) < 50) return;

          this.navigateModal(delta > 0 ? 1 : -1);
        }, { passive: true });
      }

      initModalDrag() {
        // The drag handle itself is CSS-gated to desktop (`display: none` on
        // mobile), so binding `mousedown` unconditionally is safe and survives
        // breakpoint changes — the handler body runs only when the handle is
        // actually clickable.
        const handle = this.modal.querySelector('.madara-product-ugc__modal-drag-handle');
        if (!handle) return;

        let startX, startY, startLeft, startTop;

        const onMove = (e) => {
          const clientX = e.touches ? e.touches[0].clientX : e.clientX;
          const clientY = e.touches ? e.touches[0].clientY : e.clientY;

          const vw = window.innerWidth;
          const vh = window.innerHeight;
          const mw = this.modal.offsetWidth;
          const mh = this.modal.offsetHeight;

          const left = Math.min(Math.max(startLeft + (clientX - startX), 0), vw - mw);
          const top  = Math.min(Math.max(startTop  + (clientY - startY), 0), vh - mh);

          this.modal.style.left   = `${left}px`;
          this.modal.style.top    = `${top}px`;
          this.modal.style.right  = 'auto';
          this.modal.style.bottom = 'auto';
        };

        const onUp = () => {
          this.modal.classList.remove('is-dragging');
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup',   onUp);
        };

        handle.addEventListener('mousedown', (e) => {
          e.preventDefault();
          const rect = this.modal.getBoundingClientRect();
          startX    = e.clientX;
          startY    = e.clientY;
          startLeft = rect.left;
          startTop  = rect.top;

          this.modal.classList.add('is-dragging');
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup',   onUp);
        });
      }

      /** Scroll to position instantly (no animation) */
      _setInstantY() {
        this._inner.scrollTo({ top: this._slotH, behavior: 'instant' });
      }

      // Measure the slot height and pin the modal to it in pixels.
      //
      // On desktop the modal uses a 16:9 ratio derived from width, capped at
      // 90% of the viewport so it stays fully visible when the page is zoomed
      // in (short viewport). On mobile we derive from the actual rendered DOM,
      // but defer one frame so the browser has a chance to finish any URL-bar
      // re-layout the body lock may have triggered. Without this defer, the
      // measurement can catch a transient in-between value and get locked in.
      _applyModalHeight() {
        const apply = () => {
          if (!this._inner) return;
          const width = this._inner.offsetWidth;
          const maxH = Math.round((window.visualViewport?.height ?? window.innerHeight) * 0.9);
          const slotH = this._breakpointMql.matches
            ? Math.min(Math.round(width * 16 / 9), maxH)
            // Prefer visualViewport on mobile — it reflects the visible area
            // even when the URL bar is animating, which offsetHeight (100dvh)
            // can lag behind.
            : Math.round(window.visualViewport?.height ?? this._inner.offsetHeight);

          if (slotH === this._slotH) return;

          this._slotH = slotH;
          this.modal.style.setProperty('--modal-height', `${slotH}px`);

          // If the modal is already populated (we're re-measuring after a
          // viewport change, not initial open), snap scroll back to the
          // centred slot so the new slotH doesn't misalign the active slide.
          if (this._modalTrackSlides && this.modal.classList.contains('is-open')) {
            this._setInstantY();
          }
        };

        if (this._breakpointMql.matches) {
          apply(); // desktop: synchronous, no URL-bar concern
        } else {
          // Double rAF: first frame flushes layout from the body lock,
          // second runs after the browser has resolved the new dvh.
          requestAnimationFrame(() => requestAnimationFrame(apply));
        }
      }

      // Keep slotH in sync with the visible viewport: the mobile URL bar
      // toggling (edge-swipe reveal on iOS, rotation, on-screen keyboard) and,
      // on desktop, browser zoom or window resize changing the 90% cap.
      _attachViewportWatcher() {
        if (this._viewportWatcherAttached) return;

        const vv = window.visualViewport;

        if (!vv) return;

        this._onViewportResize = () => this._applyModalHeight();
        vv.addEventListener('resize', this._onViewportResize);
        this._viewportWatcherAttached = true;
      }

      _detachViewportWatcher() {
        if (!this._viewportWatcherAttached) return;
        window.visualViewport?.removeEventListener('resize', this._onViewportResize);
        this._onViewportResize = null;
        this._viewportWatcherAttached = false;
      }

      // ─────────────────────────────────────────────────────────────────────
      // Modal — slot management
      // ─────────────────────────────────────────────────────────────────────

      /** Populate all three slots for a given centre index (wraps for infinite loop) */
      _loadModalSlides(index) {
        const cards = this.ugcCards;
        const total = cards.length;
        this._writeModalTrackSlide(0, cards[(index - 1 + total) % total]);
        this._writeModalTrackSlide(1, cards[index]);
        this._writeModalTrackSlide(2, cards[(index + 1) % total]);
      }

      /** Write a card's video data into a slot, or blank the slot if card is null */
      _writeModalTrackSlide(slideIndex, card) {
        const modalSlide = this._modalTrackSlides[slideIndex];
        const modalSlideVideoElement = modalSlide.querySelector('video');
        const modalSlideProductElement = modalSlide.querySelector('.madara-product-ugc__modal-carousel-product');

        modalSlide.classList.remove('is-loading');

        if (!card) {
          modalSlide.style.visibility = 'hidden';
          if (modalSlideVideoElement.src) {
            modalSlideVideoElement.pause();
            modalSlideVideoElement.removeAttribute('src');
            modalSlideVideoElement.load();
          }

          if (modalSlideProductElement) {
            modalSlideProductElement.innerHTML = ''
          }

          return;
        }

        modalSlide.style.visibility = 'visible';

        // Only reload video if the source actually changed
        if (modalSlideVideoElement.src !== card.dataset.ugcSrc) {
          modalSlideVideoElement.pause();
          modalSlideVideoElement.preload = 'auto';
          modalSlideVideoElement.src = card.dataset.ugcSrc;
          modalSlideVideoElement.poster = card.dataset.ugcPoster || '';
          modalSlideVideoElement.load();
          this._primeVideo(modalSlideVideoElement);
        }

        // Clone product block from the carousel slide template into this slide
        if (modalSlideProductElement) {
          const template = card.parentElement?.querySelector('.madara-product-ugc__slide-product');
          modalSlideProductElement.innerHTML = template ? template.innerHTML : '';
        }
      }

      /** Play slot[1]; pause all others */
      _playMiddleSlot() {
        this._modalTrackSlides.forEach((slot, i) => {
          const v = slot.querySelector('video');
          if (!v) return;
          if (i === 1) {
            this._playWhenReady(v);
          } else {
            v.pause();
          }
        });
      }

      // Force an offscreen slot's video to decode and paint its first frame.
      // Uses `loadeddata` (HAVE_CURRENT_DATA) — `loadedmetadata` only guarantees
      // duration/dimensions, not a decoded frame, so seeking there often paints nothing.
      _primeVideo(video) {
        if (!video) return;

        const seek = () => {
          if (video.paused && video.currentTime < 0.001) {
            try { video.currentTime = 0.001; } catch (_) {}
          }
        };

        if (video.readyState >= 2 /* HAVE_CURRENT_DATA */) {
          seek();
        } else {
          video.addEventListener('loadeddata', seek, { once: true });
        }
      }

      // Start playback only when the video has enough buffered data to play
      // without stalling. Prevents the black/poster flash during navigation.
      _playWhenReady(video) {
        if (!video?.src) return;

        if (video.readyState >= 3 /* HAVE_FUTURE_DATA */) {
          video.play().catch(() => {});
          return;
        }

        video.closest('.madara-product-ugc__modal-carousel-slide')?.classList.add('is-loading');

        const onReady = () => {
          video.removeEventListener('canplay', onReady);
          video.play().catch(() => {});
        };
        video.addEventListener('canplay', onReady, { once: true });
      }

      // Kick off the progress-bar animation above the product card for the
      // currently-playing middle-slot video. Mirrors `initAutoplay`'s pattern:
      // set duration CSS var, toggle off `is-playing` and force reflow, then
      // toggle it back on so the CSS transition restarts from 0%.
      _startControlsProgress(video) {
        if (!video || !this._controls) return;

        const apply = (duration) => {
          this._controls.style.setProperty('--ugc-duration', `${duration}s`);
          this._controls.classList.remove('is-playing');
          void this._controls.offsetHeight; // force reflow → restart CSS anim
          this._controls.classList.add('is-playing');
        };

        if (video.duration && video.duration > 0) {
          apply(video.duration);
        } else {
          // Duration not known yet — show playing state now, restart animation
          // once metadata arrives so the bar runs at the true video length.
          this._controls.classList.add('is-playing');
          video.addEventListener('loadedmetadata', () => {
            if (!video.paused) apply(video.duration);
          }, { once: true });
        }
      }

      // ─────────────────────────────────────────────────────────────────────
      // Modal — open / close / navigate
      // ─────────────────────────────────────────────────────────────────────

      openModalAt(index) {
        const cards = this.ugcCards;

        if (index < 0 || index >= cards.length) {
          return
        }

        this._lockBodyScroll();

        const inner = this.modal.querySelector('.madara-product-ugc__modal-inner');
        this._inner = inner;
        this._modalTrackSlides = [...this.modal.querySelectorAll('.madara-product-ugc__modal-carousel-slide')];
        this._controls = this.modal.querySelector('.madara-product-ugc__modal-controls');

        // Measure & apply modal height. Deferred one frame on mobile because
        // the body lock (position: fixed) can cause iOS/Chrome Android to
        // re-layout the URL bar; measuring synchronously catches that
        // transition and snapshots a wrong value.
        this._applyModalHeight();
        this.modalIndex = index;

        this._loadModalSlides(index);
        this._setInstantY();

        this._attachViewportWatcher();

        // Always start muted — reset both the video property and the control state
        // (video.muted persists across load() calls so must be set explicitly)
        this._modalTrackSlides?.forEach(slide => {
          const videoElement = slide.querySelector('video');
          if (videoElement) videoElement.muted = true;
        });
        this._controls.classList.remove('is-playing');
        this._controls.classList.add('is-muted');

        this.modal.classList.add('is-open');
        this._updateNavButtons();
        this._playMiddleSlot();
      }

      // Lock the document scroll on mobile while the modal is open.
      //
      // `overflow: hidden` on <body> is enough on Android/Chrome/desktop but
      // iOS Safari ignores it for touch-driven scrolls and rubber-band
      // overscroll. The reliable cross-browser technique is to take the body
      // out of the scroll flow with `position: fixed` and offset it by the
      // current scrollY, then restore both on unlock.
      _lockBodyScroll() {
        if (this._breakpointMql.matches) return;
        if (this._bodyLocked) return;

        this._scrollY = window.scrollY || window.pageYOffset || 0;
        this._prevBodyStyle = {
          position: document.body.style.position,
          top: document.body.style.top,
          left: document.body.style.left,
          right: document.body.style.right,
          width: document.body.style.width,
          overflow: document.body.style.overflow,
        };

        document.body.style.position = 'fixed';
        document.body.style.top = `-${this._scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';

        this._bodyLocked = true;
      }

      _unlockBodyScroll() {
        if (!this._bodyLocked) return;

        document.body.style.position = this._prevBodyStyle.position;
        document.body.style.top = this._prevBodyStyle.top;
        document.body.style.left = this._prevBodyStyle.left;
        document.body.style.right = this._prevBodyStyle.right;
        document.body.style.width = this._prevBodyStyle.width;
        document.body.style.overflow = this._prevBodyStyle.overflow;

        // Jump back to where the user was — the fixed-position offset
        // "froze" the visible scroll position but didn't preserve it.
        window.scrollTo(0, this._scrollY || 0);

        this._bodyLocked = false;
      }

      /** Animate track to Y; calls onComplete when the transition ends */
      _animateToY(scrollTop, onComplete) {
        let settled = false;

        // Lock user input to the scroll container so finger-drag or wheel
        // can't hijack the in-flight native smooth scroll.
        this.modal.classList.add('is-animating');

        const finish = () => {
          if (settled) return;
          // Guard against stale scrollend events fired by earlier instant scrolls
          if (Math.abs(this._inner.scrollTop - scrollTop) > 2) return;
          settled = true;
          clearTimeout(fallback);
          this._inner.removeEventListener('scrollend', finish);
          this.modal.classList.remove('is-animating');
          onComplete();
        };

        // Fallback: if scrollend never fires (older Safari) or stale events
        // keep blocking it, force-complete after 700 ms
        const fallback = setTimeout(() => {
          if (settled) return;
          settled = true;
          this._inner.removeEventListener('scrollend', finish);
          this.modal.classList.remove('is-animating');
          onComplete();
        }, 700);

        // Not { once: true } — we may need to skip stale firings and wait
        // for the one where scrollTop actually matches the target
        this._inner.addEventListener('scrollend', finish);
        this._inner.scrollTo({ top: scrollTop, behavior: 'smooth' });
      }

      navigateModal(delta, shouldVideoPlay) {
        if (this._isNavigating) return;

        const total = this.ugcCards.length;

        if (total < 2) {
          return; // nothing to loop through
        }

        this._isNavigating = true;
        const next  = ((this.modalIndex + delta) % total + total) % total;
        const cards = this.ugcCards;

        // Remember the outgoing video's play state for the incoming video.
        // Outgoing is intentionally left playing during the scroll for a
        // seamless audio/visual transition; it gets paused/reloaded when its
        // slot is rewritten by `_writeModalTrackSlide` after the swap.
        const outgoingVideo = this._modalTrackSlides[1]?.querySelector('video');
        const wasPlaying = typeof shouldVideoPlay === 'boolean'
          ? shouldVideoPlay
          : outgoingVideo ? !outgoingVideo.paused : true;

        // Forward → slot[2] slides in; backward → slot[0] slides in
        const incomingIndex = delta > 0 ? 2 : 0;
        const targetY = delta > 0 ? 2 * this._slotH : 0;

        const incomingVideo = this._modalTrackSlides[incomingIndex]?.querySelector('video');

        if (incomingVideo?.src && wasPlaying) {
          this._playWhenReady(incomingVideo);
        }

        this._animateToY(targetY, () => {
          // Move the incoming video node into the middle slot — no reload, no flicker
          const incomingSlot = this._modalTrackSlides[incomingIndex];
          const middleSlot = this._modalTrackSlides[1];
          const incomingProduct = incomingSlot.querySelector('.madara-product-ugc__modal-carousel-product');
          const outgoingProduct = middleSlot.querySelector('.madara-product-ugc__modal-carousel-product');

          middleSlot.replaceChild(incomingVideo, outgoingVideo);
          incomingSlot.appendChild(outgoingVideo);

          if (incomingProduct && outgoingProduct) {
            middleSlot.replaceChild(incomingProduct, outgoingProduct);
            incomingSlot.appendChild(outgoingProduct);
          }

          // Snap — slot[1] already has the correct video
          this._setInstantY();

          this.modalIndex = next;
          if (wasPlaying) {
            this._startControlsProgress(middleSlot.querySelector('video'));
          } else {
            this._controls?.classList.remove('is-playing');
          }
          this._updateNavButtons();

          // Update the now-offscreen prev/next slots after this frame
          // so their content change is never visible.
          // _isNavigating stays true until slots are ready so no
          // second navigation can sneak in during this window.
          requestAnimationFrame(() => {
            const cardsLength = cards.length;
            this._writeModalTrackSlide(0, cards[(next - 1 + cardsLength) % cardsLength]);
            this._writeModalTrackSlide(2, cards[(next + 1) % cardsLength]);
            this._isNavigating = false;
          });
        });
      }

      closeModal() {
        this.modal.classList.remove('is-open');
        // Safety: clear animation state in case close interrupted a nav.
        this.modal.classList.remove('is-animating');
        this._isNavigating = false;
        this._wheelAccum = 0;
        this._wheelCooldown = 0;

        // Clear desktop drag inline positioning so the next open on a different
        // viewport (or after a drag on the same one) snaps back to the CSS
        // default instead of sticking at the last dragged location.
        this.modal.style.left = '';
        this.modal.style.top = '';
        this.modal.style.right = '';
        this.modal.style.bottom = '';

        this._unlockBodyScroll();
        this._detachViewportWatcher();
        this._modalTrackSlides?.forEach(s => {
          s.classList.remove('is-loading');
          s.querySelector('video')?.pause();
        });

        // Detach video sources after the close transition so the browser stops
        // buffering. Guarded against a quick re-open within the 300 ms window.
        setTimeout(() => {
          if (this.modal.classList.contains('is-open')) return;
          this._modalTrackSlides?.forEach(s => {
            const v = s.querySelector('video');
            if (v) { v.removeAttribute('src'); v.load(); }
          });
        }, 300);
      }

      _updateNavButtons() {
        // Infinite loop — both buttons are always active
        const prev = this.modal.querySelector('.madara-product-ugc__modal-prev');
        const next = this.modal.querySelector('.madara-product-ugc__modal-next');
        if (prev) prev.disabled = false;
        if (next) next.disabled = false;
      }
    }
  );
}
